// src/routes/Guards.jsx
'use client'

import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useTeam } from '@/context/TeamContext'
import PageTransitionLoader from '@/components/PageTransitionLoader'

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'

/* -------------------------------------------
   Small helpers
--------------------------------------------*/

function safeSetSession(key, value) {
  try {
    sessionStorage.setItem(key, value)
  } catch {}
}

function getPath(location) {
  return `${location.pathname}${location.search}${location.hash || ''}`
}

/* -------------------------------------------
   RequireAuth – must be logged in
--------------------------------------------*/
export function RequireAuth({ children }) {
  const location = useLocation()
  const { isAuthenticated, loadingAuth, profileLoading } = useAuth()

  const path = getPath(location)

  if (loadingAuth || profileLoading) {
    return <PageTransitionLoader />
  }

  if (!isAuthenticated) {
    safeSetSession(POST_LOGIN_REDIRECT_KEY, path)
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
        }}
      />
    )
  }

  return children ?? <Outlet />
}

/* -------------------------------------------
   RequireTeamSetupComplete

   Historically gated the main app shell until a user finished an
   onboarding wizard. Onboarding has been removed — new users land on
   /teams and create a team inline. This guard now just waits for auth
   and team data to hydrate, then passes through. Kept as a named export
   so App.jsx doesn't need restructuring.
--------------------------------------------*/
export function RequireTeamSetupComplete({ children }) {
  const { loadingAuth, profileLoading } = useAuth()
  const teamCtx = useTeam()

  const teamsLoading = teamCtx?.teamsLoading ?? false
  const effectiveLoading = loadingAuth || profileLoading || teamsLoading

  if (effectiveLoading) {
    return <PageTransitionLoader />
  }

  return children ?? <Outlet />
}

/* -------------------------------------------
   RequireCoachRole

   Wraps the coach-only surface (team management, season management,
   tryouts, club admin, programs, financial reports, billing upgrade,
   etc.). Parents — who legitimately have a Scrimly account because
   they accepted a parent invite — get bounced to /parent/teams when
   they hit any of these routes (whether by URL or stale link).

   This is a UI-layer redirect for UX cleanliness. The real authority
   for sensitive operations (writes) lives in Firestore rules + Cloud
   Function role checks; do not rely on this guard for security.

   Usage in App.jsx:
     <Route element={<RequireCoachRole />}>
       <Route path="/teams" element={<TeamsPage />} />
       ...all coach-only routes
     </Route>
--------------------------------------------*/
/**
 * Gate for coach-only routes. Pure parents (signup role = 'parent')
 * get bounced back to the universal home (/dashboard). Anyone else
 * — coaches, mixed-role users — passes through.
 *
 * Note: the previous "parent context" / sticky-flag / auto-flip
 * machinery has been removed. There is no longer a "parent mode";
 * role is per-team, evaluated where it matters. This guard's job
 * is just the simple "is this user globally a non-coach" check.
 */
export function RequireCoachRole({ children, fallback = '/dashboard' }) {
  const { userProfile, loadingAuth, profileLoading } = useAuth()

  if (loadingAuth || profileLoading) {
    return <PageTransitionLoader />
  }

  const role = String(userProfile?.role || '').toLowerCase()
  if (role === 'parent') {
    return <Navigate to={fallback} replace />
  }

  return children ?? <Outlet />
}
